import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse
} from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { catchError, EMPTY, Observable, tap, throwError } from "rxjs";
import { NotificationService, ServiceMessage } from "./notification.service";
import { LoggingService, LogLevel } from "./logging.service";
import { BackendHealthService, ServiceStatus } from "./backend-health.service";

@Injectable({
  providedIn: "root"
})
export class HttperrorhandlerService implements HttpInterceptor {
  private notificationService = inject(NotificationService);
  private loggingService = inject(LoggingService);
  private backendHealthService = inject(BackendHealthService);

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          this.backendHealthService.recordSuccess(request.url);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status == 401) {
          return EMPTY;
        }
        
        // Record error with BackendHealthService for monitoring
        this.backendHealthService.recordError(request.url, error.status);
        
        // Check if backend services are unavailable
        const status = this.backendHealthService.getOverallStatus();
        
        // Only show individual error notifications when all services are available
        // Otherwise, rely on the backend status notification
        if (status === ServiceStatus.AVAILABLE) {
          this.handleError("HttpErrorHandler", "messages", error, request);
        }
        
        return throwError(error);
      })
    );
  }

  public handleError(
    sourceName: string,
    topic: string,
    error: HttpErrorResponse,
    request: HttpRequest<any>
  ) {
    let errorMessage = "";
    if (error.error) {
      // A client-side or network error occurred. Handle it accordingly.
      if (error.error.message) {
        this.loggingService.log(
          sourceName,
          "An error occurred: " + error.error.message,
          LogLevel.DEBUG
        );
        errorMessage = error.error.message + "\nURL:" + request.url;
      } else if (error.error.error_description) {
        this.loggingService.log(
          sourceName,
          "An error occurred: " + error.error.error_description,
          LogLevel.DEBUG
        );
        errorMessage = error.error.error_description + "\nURL:" + request.url;
      }
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      this.loggingService.log(
        sourceName,
        `Service ${sourceName} returned code ${
          error.status
        }, body was: ${JSON.stringify(error.error)}`,
        LogLevel.DEBUG
      );
      errorMessage = error["statusText"];
    }

    let level = "error";
    let title = "Error";

    // Handle specific error codes
    if (error["status"] in [401, 403, 404, 409, 500, 503, 504]) {
      if (error["status"] === 401) {
        errorMessage =
          "You are not authorized to access " +
          request.url +
          ".\nPlease login or refresh your session and try again!";
        level = "warn";
        title = "Warning";
      } else if (error["status"] === 403) {
        errorMessage =
          "You are not authorized to access " +
          request.url +
          ".\nPlease login or refresh your session and try again!";
        level = "warn";
        title = "Warning";
      } else if (error["status"] === 404) {
        errorMessage =
          "Backend service or resource unavailable for " +
          request.url +
          ".\nPlease try again later!";
        level = "warn";
        title = "Warning";
      } else if (error["status"] === 409) {
        errorMessage =
          "The resource you are trying to add or modify for " +
          request.url +
          " already exists and can not be replaced or updated.";
        level = "warn";
        title = "Warning";
      } else if (error["status"] in [500, 503, 504]) {
        errorMessage =
          "Backend service or resource unavailable for " +
          request.url +
          ".\nPlease try again later!";
        level = "error";
        title = "Error";
      }
    } else if (errorMessage === "") {
      if (error["status"] === 200) {
        errorMessage =
          "Database is empty" + ".\nPlease import data or try again later!";
        level = "info";
        title = "Info";
      } else {
        errorMessage = "An unknown error occurred!";
        level = "error";
        title = "Error";
      }
    }

    // Log the final error message
    this.loggingService.log(
      sourceName,
      `Final error message: ${errorMessage}`,
      LogLevel.DEBUG
    );

    // Return an observable with a user-facing error message.
    this.notificationService.publish(topic, level, title, errorMessage);
  }

  /**
   * Record a successful HTTP response for backend health monitoring
   * @param url The URL that was successfully accessed
   */
  public recordSuccess(url: string): void {
    this.backendHealthService.recordSuccess(url);
  }
}
